import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { api } from "../../utils/api";
import SearchInput from "../common/SearchInput";
import { TableSkeleton } from "../common/Skeleton";
import EmptyState from "../common/EmptyState";
import styles from "./AuditLog.module.css";

const PAGE = 15;

const fetchAudit = async ({ page, q }) =>
  (await api.get("audit/notes", { params: { page, limit: PAGE, q: q || undefined } })).data;

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const StatusPill = ({ status }) => {
  const map = {
    pending:    "status-pending",
    processing: "status-pending",
    minted:     "status-active",
    failed:     "status-burnt",
  };
  return <span className={`status-pill ${map[status] || ""}`}>{status}</span>;
};

const AuditLog = () => {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [exporting, setExporting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["audit", page, q],
    queryFn: () => fetchAudit({ page, q }),
    staleTime: 30_000,
  });

  const exportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const response = await api.get("audit/notes.csv", {
        params: q ? { q } : {},
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "text/csv;charset=utf-8" }));
      const filename = `cmbsdlt-audit-${new Date().toISOString().slice(0, 10)}.csv`;
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Audit log exported");
    } catch (err) {
      console.error("CSV export failed:", err);
      toast.error("Failed to export audit log");
    } finally {
      setExporting(false);
    }
  };

  const items = data?.items || [];
  const total = data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE));

  return (
    <section className="card" style={{ padding: 0, overflow: "hidden" }}>
      <header className={styles.head}>
        <div>
          <h2 className={styles.title}>Note edit audit log</h2>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            Every edit/mint/burn action recorded for compliance review.
          </p>
        </div>
        <div className={styles.headActions}>
          <SearchInput value={q} onChange={(v) => { setPage(1); setQ(v); }} placeholder="Search by note, actor, deal..." />
          <button
            type="button"
            className="btn btn-ghost"
            onClick={exportCsv}
            disabled={exporting || !data || data.totalCount === 0}
            title="Download audit log as CSV (up to 10,000 most-recent entries)"
          >
            <i className={`bi ${exporting ? "bi-arrow-repeat" : "bi-download"}`}
               style={exporting ? { animation: "spin 0.7s linear infinite" } : undefined} />
            {" "}Export CSV
          </button>
        </div>
      </header>

      {isLoading ? (
        <div style={{ padding: 16 }}><TableSkeleton rows={6} cols={5} /></div>
      ) : items.length === 0 ? (
        <EmptyState
          icon="bi-shield-check"
          title="No edits yet"
          subtitle="Note edits will appear here as soon as someone makes changes."
        />
      ) : (
        <>
          <div className={styles.tableWrap}>
            <table className="data-table" style={{ margin: 0, borderRadius: 0, border: "none" }}>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Actor</th>
                  <th>Note</th>
                  <th>Deal</th>
                  <th>Status</th>
                  <th style={{ textAlign: "right" }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id}>
                    <td>{formatDate(row.updatedAt)}</td>
                    <td>{row.actorEmail || <span className="muted">system</span>}</td>
                    <td><span className={styles.noteName}>{row.noteName}</span></td>
                    <td>{row.deal || <span className="muted">—</span>}</td>
                    <td><StatusPill status={row.status} /></td>
                    <td style={{ textAlign: "right", fontFamily: "var(--font-serif)" }}>
                      {row.balance != null ? `$${Number(row.balance).toLocaleString()}` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className={styles.foot}>
              <button className="arrow-button" disabled={page === 1} onClick={() => setPage(page - 1)}>&larr;</button>
              <span className="muted" style={{ fontSize: 12 }}>Page {page} of {totalPages} · {total} entries</span>
              <button className="arrow-button" disabled={page === totalPages} onClick={() => setPage(page + 1)}>&rarr;</button>
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default AuditLog;
